import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { isReadonlyArray } from "@tsmono/util";

import {
  useScrollDirection,
  type UseScrollDirectionOptions,
} from "./useScrollDirection";

// Keys that natively scroll the focused element's nearest scrollable
// ancestor — the same set VirtualList's own SCROLL_KEYS treats as "real user
// input" for follow-state purposes (see VirtualList.tsx). Shared here (and
// imported by VirtualList) so the two can't drift apart.
export const SCROLL_RELEASE_KEYS = new Set([
  "ArrowUp",
  "ArrowDown",
  "PageUp",
  "PageDown",
  "Home",
  "End",
  " ",
]);

/**
 * Releases "chrome nav ownership" back to natural scroll-direction detection
 * on a real user gesture. While `navOwnsRef.current` is true, a navigation
 * action (deep link, j/k/f/h/l, go-to-turn) owns whatever chrome-collapse
 * signal reads the ref, suppressing natural detection so programmatic
 * scrolls can't flicker it. A physical user gesture hands ownership back:
 * wheel/touch, or any of the keys that natively scroll the focused element's
 * nearest scrollable ancestor (Home/End/PageUp/PageDown/Arrow/Space) — this
 * matters even when focus sits on a plain button inside the scroller (e.g. a
 * turn-nav chevron) rather than the scroller itself, since the browser still
 * scrolls the container natively without the button consuming the key.
 *
 * One shared implementation for every "nav owns the chrome" call site.
 */
export function useChromeNavOwnershipRelease(
  navOwnsRef: RefObject<boolean>,
  scrollRef: RefObject<HTMLElement | null>
): void {
  useEffect(() => {
    const release = (target: EventTarget | null) => {
      if (!navOwnsRef.current) return;
      const container = scrollRef.current;
      if (container && target instanceof Node && container.contains(target)) {
        navOwnsRef.current = false;
      }
    };
    // Wheel/touch only — NOT clicks: an outline/lane click is navigation (it
    // reclaims ownership itself), not a hand-back to natural scrolling.
    const onWheelOrTouch = (e: Event) => release(e.target);
    const onKeyDown = (e: KeyboardEvent) => {
      if (SCROLL_RELEASE_KEYS.has(e.key)) release(e.target);
    };
    const opts = { capture: true, passive: true } as const;
    window.addEventListener("wheel", onWheelOrTouch, opts);
    window.addEventListener("touchmove", onWheelOrTouch, opts);
    window.addEventListener("keydown", onKeyDown, opts);
    return () => {
      window.removeEventListener("wheel", onWheelOrTouch, opts);
      window.removeEventListener("touchmove", onWheelOrTouch, opts);
      window.removeEventListener("keydown", onKeyDown, opts);
    };
  }, [navOwnsRef, scrollRef]);
}

/** A chrome-collapse setter driven by {@link useChromeNavOwnership}'s
 *  forceHidden (and realigned on a resetKey change) that lives OUTSIDE the
 *  hook's own scroll-direction state — e.g. a host header whose
 *  useScrollDirection instance belongs to another component. */
export interface ChromeTarget {
  /** Current hidden state when the caller has it (skips redundant sets). */
  hidden?: boolean;
  setHidden: (hidden: boolean) => void;
  /** Expand (hidden=false) only when the scroller is at its very top: chrome
   *  that only re-expands at the top (a stayHiddenOnUpScroll header) must
   *  collapse on every force (nav landing, find-forward) but expand only for
   *  `k` past turn 1 — never for find-prev mid-log. */
  expandOnlyAtTop?: boolean;
}

export interface UseChromeNavOwnershipOptions {
  /** Whether the CURRENT key's mount is nav-owned (deep-link presence). Read
   *  once at mount and re-read when `resetKey` changes. */
  ownedForKey: () => boolean;
  /** Changes when the same mounted elements host a new visit (sibling hops:
   *  the route element and scroll container both survive). Ownership and
   *  every chrome's hidden state re-derive from the current params in the
   *  render phase, before paint. */
  resetKey?: string | null;
  /** Freeze natural detection while find-in-page scrolls matches into view.
   *  Deliberately opt-in: a surface whose chrome should follow find-driven
   *  scrolling omits it. */
  findActiveRef?: RefObject<boolean>;
  /** Adopt the host's shared ownership ref instead of creating one (the
   *  inspect sample header and transcript swimlanes share ownership). */
  navOwnsRef?: RefObject<boolean>;
  /** Forwarded to the wrapped useScrollDirection (threshold, one-way mode). */
  scrollDirection?: Omit<
    UseScrollDirectionOptions,
    "suppressRef" | "initialHidden"
  >;
  /** Gate this hook's own chrome expand on the scroller being at its top
   *  (see {@link ChromeTarget.expandOnlyAtTop}). */
  expandOnlyAtTop?: boolean;
  /** Extra chrome forceHidden drives and a resetKey change realigns. */
  extraTargets?: ReadonlyArray<ChromeTarget>;
}

export interface UseChromeNavOwnershipResult {
  /** The wrapped useScrollDirection's hidden state (natural detection,
   *  suppressed while nav or find owns the chrome). */
  hidden: boolean;
  resetAnchor: (debounce?: boolean) => void;
  /** Raw setter — no ownership claim, no at-top gate (resets/realignment). */
  setHidden: (hidden: boolean) => void;
  navOwnsRef: RefObject<boolean>;
  suppressRef: RefObject<boolean>;
  /** Force the chrome the way an equivalent manual scroll would set it:
   *  claims ownership, collapses every target, expands per-target only at
   *  the top where the target demands it. */
  forceHidden: (hidden: boolean) => void;
}

/**
 * Nav-owned chrome state for a scrollable surface: wraps useScrollDirection
 * with the ownership contract shared by the inspect sample page, the scout
 * transcript page, and the scout scanner-result page. Navigation (deep
 * links, f/h/j/k/l, go-to-turn) FORCES the chrome state and, while it owns
 * it, natural scroll-direction detection is fully suppressed — j/k can never
 * flicker the chrome, and once collapsed it stays collapsed until `kkk`
 * reaches the very top (which forces expand). A physical user gesture on the
 * scroller (wheel/touch/scroll keys) hands ownership back to the natural
 * world, which resumes fresh from the current position.
 */
export function useChromeNavOwnership(
  scrollRef:
    | RefObject<HTMLElement | null>
    | ReadonlyArray<RefObject<HTMLElement | null>>,
  options: UseChromeNavOwnershipOptions
): UseChromeNavOwnershipResult {
  const { ownedForKey, findActiveRef, expandOnlyAtTop, extraTargets } = options;
  const resetKey = options.resetKey ?? null;

  const [initialOwned] = useState(ownedForKey);
  const localNavOwnsRef = useRef(initialOwned);
  const navOwnsRef = options.navOwnsRef ?? localNavOwnsRef;
  const suppressRef = useMemo(
    () => ({
      get current() {
        return (findActiveRef?.current ?? false) || navOwnsRef.current;
      },
    }),
    [findActiveRef, navOwnsRef]
  );

  const { hidden, resetAnchor, setHidden } = useScrollDirection(scrollRef, {
    ...options.scrollDirection,
    suppressRef,
    // A nav-owned mount lands scrolled down — start collapsed instead of
    // painting the chrome expanded for a frame and blinking it away. Bare
    // mounts start expanded, statically (no state flip on load).
    initialHidden: initialOwned,
  });

  // The release-gesture container is the primary (first) scroller.
  const primaryRef = isReadonlyArray(scrollRef) ? scrollRef[0]! : scrollRef;
  useChromeNavOwnershipRelease(navOwnsRef, primaryRef);

  const extraTargetsRef = useRef(extraTargets);
  useEffect(() => {
    extraTargetsRef.current = extraTargets;
  });

  // Re-derive ownership + chrome for a new visit over the same mounted
  // elements, in the render phase so it lands before paint — the wrapped
  // hook's scroller-changed reset can't fire because the scroll element
  // never remounts across the hop.
  const [resetForKey, setResetForKey] = useState(resetKey);
  if (resetKey !== resetForKey) {
    setResetForKey(resetKey);
    const startsCollapsed = ownedForKey();
    // eslint-disable-next-line react-hooks/refs, react-hooks/immutability -- deliberate render-phase reset of the (possibly host-shared) ownership ref: must land in the SAME render the key changes, or the old visit's chrome state paints for a frame before this corrects it
    navOwnsRef.current = startsCollapsed;
    if (hidden !== startsCollapsed) setHidden(startsCollapsed);
    for (const target of extraTargets ?? []) {
      if (target.hidden !== startsCollapsed) target.setHidden(startsCollapsed);
    }
  }

  const forceHidden = useCallback(
    // eslint-disable-next-line react-hooks/immutability -- ownership IS a shared mutable ref (possibly adopted from the host); claiming it on every force is the contract
    (hiddenNext: boolean) => {
      // Every force claims ownership (suppressing natural detection).
      // eslint-disable-next-line react-hooks/immutability -- see above
      navOwnsRef.current = true;
      const atTop = (primaryRef.current?.scrollTop ?? 0) <= 0;
      if (hiddenNext || !expandOnlyAtTop || atTop) setHidden(hiddenNext);
      for (const target of extraTargetsRef.current ?? []) {
        if (!hiddenNext && target.expandOnlyAtTop && !atTop) continue;
        target.setHidden(hiddenNext);
      }
    },
    [navOwnsRef, primaryRef, expandOnlyAtTop, setHidden]
  );

  return {
    hidden,
    resetAnchor,
    setHidden,
    navOwnsRef,
    suppressRef,
    forceHidden,
  };
}
